import React, { useState } from "react";
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Box,
    List,
    ListItem,
    makeStyles,
    Step,
    StepConnector,
    StepContent,
    StepLabel,
    Stepper,
    Typography,
} from "@material-ui/core";
import {
    Identifier,
    useQueryWithStore,
    useResourceContext,
} from "react-admin";
import { HistoryOutlined, PlusOneOutlined } from "@material-ui/icons";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import { Log } from "./shared/types";

const useEventStyles = makeStyles({
    accordion: {
        boxShadow: "none",
        border: "1px solid #ddd",
        backgroundColor: "#fafafa",
        minWidth: 180,
        maxWidth: 600,
    },
    summary: {
        minHeight: 32,
        height: 32,
        padding: "0 8px",

        "& .MuiAccordionSummary-content": {
            margin: 0,
            alignItems: "center",
        },

        "&.Mui-expanded": {
            minHeight: 32,
        },
    },
    details: {
        padding: "4px 8px 8px 8px",
        maxHeight: 300,
        overflow: "auto",
        display: "block",
    },
    stepper: {
        background: "none",
        border: "none",
        padding: 0,
    },
    listItem: {
        textTransform: "capitalize",
        padding: "0px 0px 0px 16px",
        display: "block",
    },
});

const EventList = ({ id }: { id: Identifier }) => {
    const resource = useResourceContext();
    const classes = useEventStyles();

    const { data, loading, error } = useQueryWithStore({
        type: "getHistory",
        resource,
        payload: { id },
    });

    const events: Log[] = Array.isArray(data)
        ? data
        : data?.data || [];

    if (loading) {
        return (
            <Typography variant="body2" color="textSecondary">
                Caricamento logs...
            </Typography>
        );
    }

    if (error) {
        return (
            <Typography variant="body2" color="error">
                Errore caricamento logs
            </Typography>
        );
    }

    if (!events.length) {
        return (
            <Typography variant="body2" color="textSecondary">
                Nessun log
            </Typography>
        );
    }

    return (
        <Stepper
            orientation="vertical"
            classes={{ root: classes.stepper }}
            connector={<StepConnector style={{ minHeight: 8 }} />}
        >
            {events.map((event: Log, index: number) => {
                const changedFields = event.changed_fields || [];

                return (
                    <Step key={index} expanded active completed>
                        <StepLabel
                            StepIconComponent={() => {
                                const Component =
                                    event.history_type === "Creato"
                                        ? PlusOneOutlined
                                        : HistoryOutlined;

                                return (
                                    <Component
                                        fontSize="small"
                                        color="disabled"
                                        style={{ paddingLeft: 3 }}
                                    />
                                );
                            }}
                        >
                            <Typography variant="caption">
                                <b>
                                    {event.history_user || "Sistema"}
                                </b>{" "}
                                {new Date(event.history_date).toLocaleString()}
                            </Typography>
                        </StepLabel>

                        <StepContent style={{ marginTop: 0 }}>
                            {changedFields.length > 0 ? (
                                <List style={{ padding: 0 }}>
                                    {changedFields.map(
                                        (item: string, itemIndex: number) => (
                                            <ListItem
                                                key={itemIndex}
                                                className={classes.listItem}
                                            >
                                                <Typography
                                                    dangerouslySetInnerHTML={{
                                                        __html: item,
                                                    }}
                                                    variant="body2"
                                                    color="textSecondary"
                                                />
                                            </ListItem>
                                        )
                                    )}
                                </List>
                            ) : (
                                <Typography
                                    variant="body2"
                                    color="textSecondary"
                                    style={{ paddingLeft: 16 }}
                                >
                                    Nessun campo modificato
                                </Typography>
                            )}
                        </StepContent>
                    </Step>
                );
            })}
        </Stepper>
    );
};

export const HistoryField = ({ record }: { record?: any }) => {
    const [open, setOpen] = useState(false);
    const classes = useEventStyles();

    if (!record) {
        return null;
    }

    return (
        <Accordion
            expanded={open}
            onChange={() => setOpen((value) => !value)}
            className={classes.accordion}
        >
            <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                className={classes.summary}
            >
                <Box display="flex" alignItems="center">
                    <HistoryOutlined
                        fontSize="small"
                        style={{ marginRight: 4 }}
                    />

                    <Typography variant="body2">
                        Logs
                    </Typography>
                </Box>
            </AccordionSummary>

            <AccordionDetails className={classes.details}>
                {open && <EventList id={record.id} />}
            </AccordionDetails>
        </Accordion>
    );
};

HistoryField.defaultProps = {
    label: "Logs",
};